import React, { Component } from 'react';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import { Flex } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ExperienceFailure, ExperienceSuccess } from '@atlassian/experience-tracker';
import { injectIntlV2 as injectIntl } from '@atlassian/jira-intl/src/v2/inject.tsx';
import type { IntlShapeV2 as IntlShape } from '@atlassian/jira-intl/src/v2/types.tsx';
import type { Recipient } from '@atlassian/jira-platform-opsgenie-schedule';
import ScheduleTimeline from '@atlassian/jira-platform-opsgenie-schedule-timeline/src/ui/index.tsx';
import SchedulesService from '@atlassian/jira-platform-opsgenie-schedule/src/services/index.tsx';
import { FireTrackAnalytics, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { TenantContextSubscriber } from '@atlassian/jira-tenant-context-controller/src/components/tenant-context/index.tsx';
import { PAGE_VIEW_EXP_NAME } from '../../../common/constant';
import { PerformanceTracker } from '../../../services/performance-tracker';
import OpsgenieXflowSvg from '../../assets/megaphone-opsgenie-xflow.svg';
import messages from './messages';
import OpsgenieScheduleError from './schedule-error';
import { assignColor } from './utils';

const DEFAULT_INSTANCE_URL = 'https://app.opsgenie.com';

type Props = {
	isAdmin: boolean;
	baseUrl: string;
	cloudId: string;
	teamId: string;
	projectId: string | number;
	intl: IntlShape;
};

type States = {
	colorMap: Map<string, string>;
};

// eslint-disable-next-line jira/react/no-class-components
class OpsgenieScheduleComponent extends Component<Props, States> {
	state = {
		colorMap: new Map<string, string>(),
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onCreateScheduleButtonClicked = (_: any, analyticsEvents: any) => {
		fireUIAnalytics(analyticsEvents, 'button clicked', 'createScheduleButton');
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	onCreateRotationButtonClicked = (_: any, analyticsEvents: any) => {
		fireUIAnalytics(analyticsEvents, 'button clicked', 'createRotationButton');
	};

	onGetBackgroundColor = ({ id, name }: Recipient) => {
		if (name && name === '--') {
			return token('color.background.neutral', colors.N30);
		}
		const { colorMap } = this.state;
		return assignColor(id || 'unknown', colorMap);
	};

	render() {
		const { baseUrl, cloudId, teamId, isAdmin, projectId } = this.props;
		const { formatMessage } = this.props.intl;

		return (
			<TenantContextSubscriber>
				{({ tenantContext }) => (
					<SchedulesService {...{ cloudId, baseUrl, teamId }}>
						{/* @ts-expect-error - TS7031 - Binding element 'data' implicitly has an 'any' type. | TS7031 - Binding element 'loading' implicitly has an 'any' type. | TS7031 - Binding element 'error' implicitly has an 'any' type. */}
						{({ data, loading, error }) => {
							const instanceUrl = data ? data.instanceUrl : null;
							if (error) {
								if (error.statusCode === 403) {
									return (
										<>
											<FireTrackAnalytics
												eventName="emptyState viewed"
												actionSubjectId="forbiddenEmptyState"
											/>
											<ExperienceSuccess
												name={PAGE_VIEW_EXP_NAME}
												attributes={{
													noAccess: true,
												}}
											/>
											<EmptyState
												header={formatMessage(messages.accessForbiddenTitle)}
												description={formatMessage(messages.accessForbiddenBody)}
												imageUrl={OpsgenieXflowSvg}
											/>
										</>
									);
								}

								return (
									<>
										<ExperienceFailure
											name={PAGE_VIEW_EXP_NAME}
											error={new Error(`Unable to load on call schedule data. ${error.message}`)}
											// ExperienceFailure component seems to have a bug that it doesn't report error message to the analytics.
											// It will be fixed by the owner, but in the meantime, we will add it here. Remove this when the platform is fixed.
											attributes={{ errorMessageManual: error.message }}
										/>
										<OpsgenieScheduleError
											isAdmin={isAdmin}
											instanceUrl={instanceUrl || DEFAULT_INSTANCE_URL}
											teamId={teamId}
										/>
									</>
								);
							}

							if (loading || !data) {
								return (
									<Flex justifyContent="center">
										{/* TODO: Refactor this to avoid the need for a hardcoded value */}
										{/* eslint-disable-next-line jira/react/no-style-attribute, @atlaskit/ui-styling-standard/enforce-style-prop */}
										<div style={{ paddingTop: '220px' }}>
											<Spinner size="large" />
										</div>
									</Flex>
								);
							}

							const { schedules, numOfRotations } = data;

							if (schedules.length === 0) {
								return (
									<>
										<PerformanceTracker />
										<FireTrackAnalytics
											eventName="emptyState viewed"
											actionSubjectId="noSchedulesEmptyState"
										/>
										<ExperienceSuccess
											name={PAGE_VIEW_EXP_NAME}
											attributes={{
												isEmpty: true,
											}}
										/>
										<EmptyState
											header={formatMessage(messages.createScheduleTitle)}
											description={formatMessage(messages.createScheduleBody)}
											imageUrl={OpsgenieXflowSvg}
											primaryAction={
												<Button
													appearance="primary"
													href={`${instanceUrl || ''}/teams/dashboard/${teamId}`}
													isDisabled={!instanceUrl}
													target="_blank"
													onClick={this.onCreateScheduleButtonClicked}
												>
													{formatMessage(messages.createScheduleButtonText)}
												</Button>
											}
										/>
									</>
								);
							}

							if (numOfRotations === 0) {
								return (
									<>
										<PerformanceTracker />
										<FireTrackAnalytics
											eventName="opsgenieSchedule viewed"
											attributes={{
												numOfSchedules: schedules.length,
												numOfRotations,
												projectId,
											}}
										/>
										<ExperienceSuccess
											name={PAGE_VIEW_EXP_NAME}
											attributes={{
												isEmpty: true,
											}}
										/>
										<ScheduleTimeline
											scheduleItem={schedules[0]}
											{...{
												instanceName: tenantContext.cloudName,
												teamId,
												instanceUrl,
											}}
											withSpotlight
											onGetBackgroundColor={this.onGetBackgroundColor}
										/>
										<FireTrackAnalytics
											eventName="emptyState viewed"
											actionSubjectId="noRotationsEmptyState"
										/>
										<EmptyState
											header={formatMessage(messages.emptyScheduleTitle)}
											description={formatMessage(messages.emptyScheduleBody)}
											imageUrl={OpsgenieXflowSvg}
											primaryAction={
												<Button
													appearance="primary"
													href={`${instanceUrl || ''}/settings/schedule/detail/${
														schedules[0].schedule.id
													}`}
													isDisabled={!instanceUrl}
													target="_blank"
													onClick={this.onCreateRotationButtonClicked}
												>
													{formatMessage(messages.emptyScheduleButtonText)}
												</Button>
											}
										/>
									</>
								);
							}

							return (
								<>
									<PerformanceTracker />
									<FireTrackAnalytics
										eventName="opsgenieSchedule viewed"
										attributes={{
											numOfSchedules: schedules.length,
											numOfRotations,
											projectId,
										}}
									/>
									<ExperienceSuccess
										name={PAGE_VIEW_EXP_NAME}
										attributes={{
											isEmpty: false,
											numOfSchedules: schedules.length,
											numOfRotations,
											projectId,
										}}
									/>
									{/* @ts-expect-error - TS7006 - Parameter 'schedule' implicitly has an 'any' type. | TS7006 - Parameter 'i' implicitly has an 'any' type. */}
									{schedules.map((schedule, i) => (
										<ScheduleTimeline
											key={schedule.schedule.id}
											scheduleItem={schedule}
											{...{
												instanceName: tenantContext.cloudName,
												instanceUrl,
												teamId,
											}}
											withSpotlight={i === 0 /* Only need spotlights for the first item */}
											onGetBackgroundColor={this.onGetBackgroundColor}
										/>
									))}
								</>
							);
						}}
					</SchedulesService>
				)}
			</TenantContextSubscriber>
		);
	}
}

export default injectIntl(OpsgenieScheduleComponent);
